import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {Storyboard, TextClip, LayerInterface, ClipDimensions, TimelineTimestamp} from "storyboard-renderer";
import {StoryboardService} from "../../../shared/storyboard.service";
import {TextEditorComponent} from "../../text-editor/text-editor.component";
import {EditModal} from "../../shared/edit-modal.service";
import {TimelineService} from "../../timeline/timeline.service";
import {Alert} from "../../../../shared/alert/Alert";
import {AlertService} from "../../../../shared/alert/alert.service";

@Component({
  selector: 'app-transcribe-editor',
  templateUrl: './transcribe-editor.component.html',
  styleUrls: ['./transcribe-editor.component.scss']
})
export class TranscribeEditorComponent implements OnInit {

  @Input() storyboard: Storyboard;
  @Output() onCloseSRTEditor = new EventEmitter();

  sentences: TextClip[] = [];
  storyboardSaving = false;

  constructor(private storyboardService: StoryboardService, private modalService: EditModal,
              private timelineService: TimelineService, private alertService: AlertService) { }

  ngOnInit() {
    this.sentences = this.getTranscriptionTextClips();
  }

  private getTranscriptionTextClips() {

    const sentences = [];
    for(let i=0; i<this.storyboard.timeline.layers.length; i++) {
      let clips = this.storyboard.timeline.layers[i].getClips();

      for(let j=0; j<clips.length; j++) {
        if(clips[j] instanceof TextClip && (clips[j] as TextClip).transcription) {
          sentences.push(clips[j]);
        }
      }
    }

    return sentences;
  }

  deleteSentence(sentence: TextClip) {
    const layer = this.findLayerByTextClip(sentence);

    (layer as any).clips = (layer as any).clips.filter(clip => clip !== sentence);
    this.sentences = this.sentences.filter(clip => clip !== sentence);

    // Trigger Storyboard Object Change
    this.timelineService.storyboardChangeEvent.next(this.storyboard);
  }

  breakSentence(params) {
    const layer = this.findLayerByTextClip(params.sentence);

    const clip = new TextClip(null);

    const nextClip = this.findNextSentence(params.sentence);
    const nextIndexOnLayer = (layer as any).clips.findIndex(clip => clip === params.sentence);

    let startAt;
    let duration;
    console.log("SENTENCE", params);
    let timingIndex = this.findTimingBySentence(params.sentence, params.cursorPosition);

    let newTiming = [];

    if(timingIndex >= 0) {
      newTiming = params.sentence.timing.splice(timingIndex);
    }

    if(newTiming.length > 0) {
      startAt = TimelineTimestamp.fromMilliseconds(newTiming[0].start * 1000).as_milliseconds;

      let lastTextTiming = this.findLastTextTiming(newTiming);
      duration = lastTextTiming.end * 1000 - startAt;

    }else if(nextClip) {
      startAt = (nextClip.starts_at.as_milliseconds - params.sentence.starts_at.as_milliseconds) / 2 + params.sentence.starts_at.as_milliseconds;
      duration = (nextClip.starts_at.as_milliseconds - params.sentence.starts_at.as_milliseconds) / 2;
    } else {
      startAt = params.sentence.starts_at.as_milliseconds + 1000;
      duration = 1000;
    }

    clip.transcription = true;
    clip.cssClass = "text-clip transcription-clip";
    clip.title = params.newText;
    clip.text = params.newText;

    clip.setStartsAt(startAt);
    clip.setDuration(duration);
    clip.copyFormat(params.sentence);
    clip.timing = newTiming;

    params.sentence.setDuration(startAt - params.sentence.starts_at.as_milliseconds);

    (layer as any).clips.splice(nextIndexOnLayer + 1, 0, clip);
    this.sentences.splice(params.sentenceIndex + 1, 0, clip);

    // Trigger Storyboard Object Change
    this.timelineService.storyboardChangeEvent.next(this.storyboard);
  }

  findLayerByTextClip(clip: TextClip) {
    for(let i=0; i<this.storyboard.timeline.layers.length; i++) {
      let clips = this.storyboard.timeline.layers[i].getClips();
      for(let j=0; j<clips.length; j++) {
        if(clips[j] instanceof TextClip && (clips[j] as TextClip).transcription && clips[j] === clip) {
          return this.storyboard.timeline.layers[i];
        }
      }
    }
  }

  findNextSentence(clip: TextClip) {
    for(let i=0; i<this.sentences.length; i++) {
      if(this.sentences[i] === clip && i + 1 < this.sentences.length) {
        return this.sentences[i + 1];
      }
    }
  }

  save(): Promise<any> {

    this.storyboardSaving = true;

    return this.storyboardService.update(this.storyboard).then(storyboard => {
      this.storyboardSaving = false;
    }, () => {
      this.storyboardSaving = false;
    });
  }

  close() {
    this.onCloseSRTEditor.emit('');
  }

  openTextEditor(clip: TextClip) {
    const modalRef = this.modalService.open(TextEditorComponent, {
      size: 'lg',
      backdrop: 'static'
    });
    modalRef.componentInstance.clip = clip;

    modalRef.result.then((result) => {
      clip.title = result.clip.text;

      if(result.applyForAll) {

        const layer = this.findLayerByTextClip(clip) as any;
        layer.clips.forEach((clip: TextClip) => clip.copyFormat(result.clip))
      }

      // Trigger Storyboard Object Change
      this.timelineService.storyboardChangeEvent.next(true);
    }, (reason) => {});
  }

  mergeWithNext(source: TextClip) {
    const nextClip = this.findNextSentence(source);

    if(!nextClip) {
      this.alertService.danger(new Alert('There is no other sentence to merge with.'));
    }

    source.title = source.title + " " + nextClip.title;
    source.text = source.text + " " + nextClip.text;
    source.dimensions = new ClipDimensions();
    source.timing.concat(nextClip.timing);

    source.setDuration(nextClip.starts_at.as_milliseconds + nextClip.duration.as_milliseconds - source.starts_at.as_milliseconds);

    this.deleteSentence(nextClip);
  }

  private findTimingBySentence(sentence, cursorPosition) {
    let characterCount = 0;
    let timingIndex = -1;
    sentence.timing.forEach((timing, index) => {
      console.log(characterCount, timing);
      if(characterCount >= cursorPosition && timingIndex < 0) {
        timingIndex = index;
      }
      characterCount += timing.text.length + 1; // + space
    });

    return timingIndex;
  }

  private findLastTextTiming(timings) {
    let timing = null;
    timings.forEach(item => {
      if(item.type != 'punctuation') {
        timing = item;
      }
    });
    return timing;
  }
}



// WEBPACK FOOTER //
// ./src/app/storyboard/storyboard-edit/transcribe/transcribe-editor/transcribe-editor.component.ts